import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';

import { getAskRovoClickedEvent } from '../askRovoAnalytics.ts';
import type { ConnectionRowAction } from '../utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

/**
 * Derives the "Ask Rovo" row action for a connections row: an icon button that opens
 * the modal's Rovo chat seeded with this row's page as context.
 *
 * Returns null unless the caller passes an `onAskRovo` handler. The modal and the preview panel
 * both thread one, so the action is absent only in the byline popup, which has no Rovo at all.
 * Threaded as a prop alongside `onPreviewOpen` (rather than via context) so both callbacks reach
 * the row-action hooks the same way. Called by `ConnectionsByLineMenuItem` for every row,
 * alongside `useSmartLinkPreviewAction`.
 */

const i18n = defineMessages({
	askRovo: {
		id: 'byline-connections.menu.item.ask-rovo',
		defaultMessage: 'Ask Rovo',
		description:
			'Aria-label / tooltip for the button on a connections row that opens Rovo chat with this connection attached as context, shown on hover.',
	},
	contextPrompt: {
		id: 'byline-connections.menu.item.ask-rovo-context-prompt',
		defaultMessage:
			'Fetch the content behind this link and use it as context for subsequent tasks:',
		description:
			"First line of the prompt the Teamwork Graph modal prefills into the Rovo chat input when the user asks Rovo about a single connection row, so the chat starts from that row's page; the connection's url is appended on the next line. The user reads it, can edit it, and presses Enter to send it, so it is translated like any other visible copy.",
	},
});

const ASK_ROVO_ACTION_ID = 'ask-rovo';

export const useAskRovoRowAction = (
	item: EnhancedSmartLink,
	onAskRovo?: (prompt?: string) => void,
): ConnectionRowAction | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		if (!onAskRovo) {
			return;
		}
		createAnalyticsEvent(
			getAskRovoClickedEvent({
				trigger: 'rowAction',
				product: item.product,
				resourceType: item.resourceType,
			}),
		).fire();

		// Prefill (not auto-send) a prompt pointing Rovo at this row's page, so the chat's
		// context is the connected page rather than the page the byline is on. The url is
		// joined on in code (not via the message) because the i18n extractor collapses a
		// newline inside a message to a space. Unseeded when the row has no url.
		onAskRovo(item.url ? [formatMessage(i18n.contextPrompt), item.url].join('\n') : undefined);
	}, [createAnalyticsEvent, formatMessage, item.product, item.resourceType, item.url, onAskRovo]);

	return useMemo(
		() =>
			onAskRovo
				? {
						id: ASK_ROVO_ACTION_ID,
						icon: RovoChatIcon,
						label: formatMessage(i18n.askRovo),
						onClick: handleClick,
					}
				: null,
		[onAskRovo, formatMessage, handleClick],
	);
};
